import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraCombobox, SpecraFormField, SpecraInlineBanner, SpecraOverlayDialog } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useTenantId } from '../../Delivery/_shared/hooks/useTenantId';
import { usePrograms } from '../../Delivery/Programs/hooks';
import { useLinkPursuitRecompete } from './recompeteHooks';
/**
 * GRW-04 item 6: "Link the contract this recompetes". Picks one of the
 * programs the viewer can read (ListPrograms is already read-filtered) and
 * calls LinkPursuitRecompete. One request id per open dialog, re-sent on
 * retry, so a retried link is never doubled.
 */ export function LinkRecompeteDialog({ captureProjectId, open, onOpenChange }) {
    const tenantId = useTenantId();
    const programs = usePrograms({
        companyId: tenantId
    });
    const link = useLinkPursuitRecompete();
    const [programId, setProgramId] = useState('');
    const [error, setError] = useState('');
    const requestId = useRef('');
    useEffect(()=>{
        if (!open) return;
        requestId.current = crypto.randomUUID();
        setProgramId('');
        setError('');
    }, [
        open
    ]);
    const options = useMemo(()=>(programs.data?.items ?? []).map((p)=>({
                value: p.id,
                label: p.programNameShort || p.programNameFull || p.contractNumber || p.id,
                description: p.contractNumber || undefined,
                keywords: [
                    p.programNameFull,
                    p.contractNumber
                ].filter(Boolean)
            })), [
        programs.data?.items
    ]);
    const submit = ()=>{
        if (!programId) return;
        setError('');
        link.mutate({
            captureProjectId,
            programId,
            requestId: requestId.current
        }, {
            onSuccess: ()=>{
                toast.success('Contract linked');
                onOpenChange(false);
            },
            onError: (err)=>setError(err instanceof Error ? err.message : 'The contract could not be linked.')
        });
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        description: "The program this pursuit competes to win again. Its contract end drives the contract clock.",
        footerProps: {
            cancelLabel: 'Cancel',
            onCancel: ()=>onOpenChange(false),
            confirmLabel: 'Link contract',
            onConfirm: submit,
            confirmDisabled: !programId,
            loading: link.isPending,
            loadingLabel: 'Linking…',
            confirmProps: {
                'data-testid': Testids.linkRecompete.submit
            }
        },
        onOpenChange: onOpenChange,
        open: open,
        size: "md",
        testId: Testids.linkRecompete.dialog,
        title: "Link the contract this recompetes",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Contract",
                    children: /*#__PURE__*/ _jsx("div", {
                        "data-testid": Testids.linkRecompete.programCombobox,
                        children: /*#__PURE__*/ _jsx(SpecraCombobox, {
                            emptyMessage: "No contracts you can read match",
                            label: "Contract",
                            loading: programs.isLoading,
                            onValueChange: (v)=>setProgramId(v ?? ''),
                            options: options,
                            placeholder: "Pick a contract",
                            searchPlaceholder: "Search by name or contract number…",
                            value: programId || undefined
                        })
                    })
                }),
                error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "danger",
                    variant: "framed",
                    children: error
                })
            ]
        })
    });
}
